import axios from 'axios';
import { getStorage, getLanguage, redirectToLogin, shouldRedirectToLogin } from 'shared/platform/adapter';
import { decodeJwtPayload } from './jwt';

const st = () => getStorage();
const getStored = (k) => st()?.getItem(k) ?? null;
const setStored = (k, v) => { const s = st(); if (s) s.setItem(k, v); };
const removeStored = (k) => { const s = st(); if (s) s.removeItem(k); };
const doRedirectToLogin = () => { if (shouldRedirectToLogin()) redirectToLogin(); };

// Функция для нормализации API URL
const normalizeApiUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return 'https://api.travmap.org';
  }
  
  // Убираем любые странные символы или кодировки
  const cleaned = url.trim();
  
  // Проверяем на правильный формат URL
  if (cleaned.startsWith('http://') || cleaned.startsWith('https://')) {
    // Проверяем на наличие странных символов (URL-encoded кириллица и т.д.)
    if (cleaned.includes('%D1%8B') || cleaned.includes('http%D1%8B') || cleaned.includes('httpы')) {
      console.warn('Invalid API URL detected, using default:', cleaned);
      return 'https://api.travmap.org';
    }
    return cleaned;
  }
  
  // Если URL не начинается с http/https, добавляем https://
  if (cleaned.includes('api.travmap.org')) {
    return 'https://api.travmap.org';
  }
  
  return 'https://api.travmap.org';
};

const API_BASE_URL = normalizeApiUrl(process.env.REACT_APP_API_URL);

// console.log('=== API CONFIGURATION ==='); // Закомментировано для продакшена
// console.log('REACT_APP_API_URL (raw):', process.env.REACT_APP_API_URL);
// console.log('API_BASE_URL (normalized):', API_BASE_URL);
// console.log('=========================');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': getLanguage() || 'en',
  },
});

// Request interceptor - обновляем Accept-Language перед каждым запросом
api.interceptors.request.use(
  (config) => {
    const currentLang = getLanguage() || 'en';
    config.headers['Accept-Language'] = currentLang;
    
    // Логируем только для запросов к routes и videos для отладки
    if (config.url && (config.url.includes('/routes/') || config.url.includes('/videos/'))) {
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with silent token refresh
let isRefreshing = false;
let pendingRequests = [];
let lastTokenRefresh = 0; // Время последнего обновления токена (timestamp)
let tokenCheckInterval = null; // Интервал для периодической проверки токена

// Функция для проверки, является ли ошибка сетевой (бэк рестартит)
const isNetworkError = (error) => {
  // Сетевые ошибки не имеют response или имеют определенные коды
  if (!error.response) {
    // ECONNREFUSED, ETIMEDOUT, ENOTFOUND и т.д.
    return true;
  }
  // 502, 503, 504 - ошибки сервера (возможно, рестарт)
  if ([502, 503, 504].includes(error.response?.status)) {
    return true;
  }
  return false;
};

// Функция для обновления Access Token используя Refresh Token с retry для сетевых ошибок
const refreshAccessToken = async (retryCount = 0) => {
  const refreshToken = getStored('refreshToken');
  if (!refreshToken) {
    return null;
  }

  const maxRetries = 3;
  const retryDelay = 1000 * (retryCount + 1); // Экспоненциальная задержка: 1s, 2s, 3s

  try {
    const refreshResp = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refreshToken: refreshToken,
    }, {
      timeout: 5000, // 5 секунд таймаут
    });
    
    const newAccessToken = refreshResp.data?.token;
    const newRefreshToken = refreshResp.data?.refreshToken; // Может быть новый при ротации
    
    if (newAccessToken) {
      setStored('token', newAccessToken);
      if (newRefreshToken) {
        setStored('refreshToken', newRefreshToken);
      }
      lastTokenRefresh = Date.now();
      return newAccessToken;
    }
    
    return null;
  } catch (error) {
    // Если refresh token невалиден, отозван или истек, очищаем все токены
    if (error.response?.status === 401 || error.response?.status === 404) {
      removeStored('token');
      removeStored('refreshToken');
      return null;
    }
    
    // Для сетевых ошибок (рестарт бэка) - делаем retry
    if (isNetworkError(error) && retryCount < maxRetries) {
      console.warn(`Failed to refresh token (network error), retrying (${retryCount + 1}/${maxRetries})...`);
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      return refreshAccessToken(retryCount + 1);
    }
    
    // Если это сетевая ошибка после всех retry, не очищаем токены
    // Пользователь останется залогиненным, токены восстановятся при следующем успешном запросе
    if (isNetworkError(error)) {
      console.error('Failed to refresh access token after retries (backend may be restarting):', error);
      // Возвращаем специальное значение, чтобы interceptor знал, что это сетевая ошибка
      throw new Error('NETWORK_ERROR');
    }
    
    // Для других ошибок тоже возвращаем null, но не очищаем токены
    console.error('Failed to refresh access token:', error);
    return null;
  }
};

// Функция для проверки и обновления токена перед истечением
const checkAndRefreshTokenIfNeeded = async (forceRefresh = false) => {
  const token = getStored('token');
  if (!token) {
    const refreshToken = getStored('refreshToken');
    if (refreshToken) {
      return await refreshAccessToken() !== null;
    }
    return false;
  }

  try {
    // Декодируем токен для проверки времени истечения
    const payload = decodeJwtPayload(token);
    if (!payload || !payload.exp) {
      const newToken = await refreshAccessToken();
      return newToken !== null;
    }
    const exp = payload.exp;
    const now = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = exp - now;

    // Если токен истек или скоро истечет, обновляем через Refresh Token
    const timeSinceLastRefresh = Date.now() - lastTokenRefresh;
    if (timeUntilExpiry <= 0 || (timeUntilExpiry < 1800 && (forceRefresh || timeSinceLastRefresh > 600000))) {
      // Используем Refresh Token для обновления
      const newToken = await refreshAccessToken();
      return newToken !== null;
    }
    
    return false;
  } catch (error) {
    // Ошибка парсинга токена - пытаемся обновить через Refresh Token
    const newToken = await refreshAccessToken();
    return newToken !== null;
  }
};

// Инициализация периодической проверки токена
const initTokenRefreshInterval = () => {
  // Очищаем предыдущий интервал, если он существует
  if (tokenCheckInterval) {
    clearInterval(tokenCheckInterval);
  }

  // Проверяем токен каждые 5 минут (300000 мс)
  tokenCheckInterval = setInterval(() => {
    const token = getStored('token');
    if (token) {
      checkAndRefreshTokenIfNeeded().catch(() => {});
    } else {
      clearTokenRefreshInterval();
    }
  }, 5 * 60 * 1000);
};

// Регистрируем window-listeners один раз на уровне модуля — они не должны дублироваться
// при повторных вызовах initTokenRefreshInterval (например, login → logout → login)
if (typeof window !== 'undefined') {
  let lastFocusCheck = 0;
  window.addEventListener('focus', () => {
    const token = getStored('token');
    const refreshToken = getStored('refreshToken');
    if (token && refreshToken) {
      const now = Date.now();
      if (now - lastFocusCheck > 60000) {
        lastFocusCheck = now;
        checkAndRefreshTokenIfNeeded(true).catch(() => {});
      }
    }
  });

  let lastVisibilityCheck = 0;
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      const token = getStored('token');
      const refreshToken = getStored('refreshToken');
      if (token && refreshToken) {
        const now = Date.now();
        if (now - lastVisibilityCheck > 60000) {
          lastVisibilityCheck = now;
          checkAndRefreshTokenIfNeeded(true).catch(() => {});
        }
      }
    }
  });
}

// Функция для очистки интервала проверки токена (вызывается при logout)
export const clearTokenRefreshInterval = () => {
  if (tokenCheckInterval) {
    clearInterval(tokenCheckInterval);
    tokenCheckInterval = null;
  }
};

// Запускаем периодическую проверку при инициализации модуля.
// Обновление токена выполняется в фоне по интервалу и при 401 в response interceptor.
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const token = getStored('token');
    if (token) {
      initTokenRefreshInterval();
    }
  }, 1000);
  
  // Перезапускаем интервал при изменении токена (например, после логина)
  const storage = st();
  if (storage && storage.setItem) {
    const originalSetItem = storage.setItem.bind(storage);
    storage.setItem = function(key, value) {
      originalSetItem(key, value);
      if (key === 'token' && value) {
        if (!tokenCheckInterval) initTokenRefreshInterval();
      } else if (key === 'token' && !value) {
        clearTokenRefreshInterval();
      }
    };
  }
}

// Request interceptor to add auth token and check if refresh needed
api.interceptors.request.use(
  async (config) => {
    const token = getStored('token');
    
    // Не делаем refresh в request interceptor: при загрузке страницы это давало гонку (один запрос
    // обновлял токен, остальные уходили со старым → 401 → второй refresh → 401 при ротации → разлогин).
    // Refresh только в response interceptor при 401 (один раз, с очередью и повтором с токеном из storage).
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers['Accept-Language'] = getLanguage() || 'en';
    
    // Отладка URL (закомментировано для продакшена)
    // console.log('=== API REQUEST DEBUG ===');
    // console.log('baseURL:', config.baseURL);
    // console.log('url:', config.url);
    // console.log('full URL:', config.baseURL + config.url);
    // console.log('========================');
    
    // Проверка на неправильные URL
    if (config.baseURL && typeof config.baseURL === 'string') {
      if (config.baseURL.includes('http%D1%8B') || config.baseURL.includes('%D1%8B')) {
        console.error('ERROR: Invalid baseURL detected!', config.baseURL);
        config.baseURL = 'https://api.travmap.org';
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // Если это ответ на refresh, login или register, сохраняем токены
    if (response.config?.url?.includes('/auth/refresh') || 
        response.config?.url?.includes('/auth/login') ||
        response.config?.url?.includes('/auth/register')) {
      const newToken = response.data?.token;
      const newRefreshToken = response.data?.refreshToken;
      if (newToken) {
        lastTokenRefresh = Date.now();
      }
      if (newRefreshToken) {
        setStored('refreshToken', newRefreshToken);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Проверяем, не связана ли ошибка с баном - если да, не делаем автоматический редирект
    const errorMessage = error.response?.data?.error || error.message || '';
    const isBannedError = errorMessage.toLowerCase().includes('banned') || 
                         errorMessage.toLowerCase().includes('заблокирован') || 
                         errorMessage.toLowerCase().includes('забанен') ||
                         errorMessage.toLowerCase().includes('account is banned');
    
    if (error.response?.status === 401 && !originalRequest.__isRetryRequest) {
      // Если ошибка связана с баном, не пытаемся обновить токен и не делаем редирект
      if (isBannedError) {
        return Promise.reject(error);
      }
      
      const oldToken = getStored('token');
      if (!oldToken) {
        removeStored('token');
        doRedirectToLogin();
        return Promise.reject(error);
      }

      // При 401 сначала повторяем запрос с текущим токеном из storage (один или два раза с паузой).
      // После refresh при загрузке страницы в storage уже новый токен — повтор пройдёт, второй refresh не вызываем.
      if (!originalRequest.__retriedWithStorage) {
        const currentToken = getStored('token');
        if (currentToken) {
          originalRequest.__retriedWithStorage = true;
          originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          return api(originalRequest);
        }
      }
      // Уже один раз повторили и снова 401 — возможно, refresh в request interceptor ещё не успел записать токен.
      // Даём 400 ms и повторяем ещё раз с getStored('token'), только потом вызываем refresh.
      if (originalRequest.__retriedWithStorage && !originalRequest.__retriedWithStorageSecond) {
        const currentToken = await new Promise((resolve) => {
          setTimeout(() => resolve(getStored('token')), 400);
        });
        if (currentToken) {
          originalRequest.__retriedWithStorageSecond = true;
          originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          return api(originalRequest);
        }
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            originalRequest.__isRetryRequest = true;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      try {
        isRefreshing = true;
        
        // Используем Refresh Token для обновления Access Token
        const newToken = await refreshAccessToken();
        if (newToken) {
          // Resolve queued requests
          pendingRequests.forEach(({ resolve }) => resolve(newToken));
          pendingRequests = [];
          isRefreshing = false;

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          originalRequest.__isRetryRequest = true;
          return api(originalRequest);
        } else {
          // Если обновление не удалось, переходим к логину
          throw new Error('Failed to refresh token');
        }
      } catch (e) {
        pendingRequests.forEach(({ reject }) => reject(e));
        pendingRequests = [];
        isRefreshing = false;
        
        // Проверяем, является ли это сетевой ошибкой (рестарт бэка)
        const isNetworkRefreshError = e.message === 'NETWORK_ERROR' || isNetworkError(e);
        
        // Проверяем, не связана ли ошибка с баном - если да, не делаем редирект
        const refreshError = e.response?.data?.error || e.message || '';
        const isBannedRefreshError = refreshError.toLowerCase().includes('banned') || 
                                    refreshError.toLowerCase().includes('заблокирован') || 
                                    refreshError.toLowerCase().includes('забанен') ||
                                    refreshError.toLowerCase().includes('account is banned');
        
        // Если это сетевая ошибка (рестарт бэка), не разлогиниваем
        if (isNetworkRefreshError) {
          console.warn('Token refresh failed due to network error (backend may be restarting), keeping tokens');
          // Возвращаем оригинальную ошибку, но не очищаем токены
          return Promise.reject(error);
        }
        
        // Очищаем токены и разлогиниваем ТОЛЬКО при 401/404 от эндпоинта refresh (токен невалиден/отозван).
        // При 502/503/500/сетевых ошибках не очищаем — после рестарта бэка пользователь остаётся залогиненным.
        if ((e.response?.status === 401 || e.response?.status === 404) && !isBannedRefreshError) {
          removeStored('token');
          removeStored('refreshToken');
          doRedirectToLogin();
          return Promise.reject(e);
        }
        // Для остальных ошибок (500, таймаут и т.д.) не очищаем токены — возможно, бэк временно недоступен
        if (e.response?.status !== 401 && e.response?.status !== 404 && !isBannedRefreshError) {
          console.warn('Token refresh failed (backend may be restarting), keeping tokens:', e.response?.status || e.message);
        }
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) =>
    api.post('/auth/register', {
      email: userData.email,
      password: userData.password,
      first_name: userData.firstName,
      last_name: userData.lastName,
      username: userData.username || '',
      locale: userData.locale || 'ru',
    }),
  resendVerification: (email, locale) =>
    api.post('/auth/resend-verification', { email, locale: locale || 'ru' }),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  getDevices: (refreshToken) => {
    const config = {};
    if (refreshToken) {
      config.params = { refreshToken };
    }
    return api.get('/auth/devices', config);
  },
  revokeDevice: (deviceId) => api.delete(`/auth/devices/${deviceId}`),
  revokeAllDevices: (refreshToken) => {
    const params = refreshToken ? { params: { refreshToken } } : {};
    return api.delete('/auth/devices', params);
  },
};

// Map API
export const mapAPI = {
  getRoutesInArea: (bounds, limit = 50, offset = 0) => {
    // Поддерживаем оба формата: Leaflet bounds объект и простой объект
    const params = bounds.getSouth ? {
      minLat: bounds.getSouth(),
      maxLat: bounds.getNorth(),
      minLng: bounds.getWest(),
      maxLng: bounds.getEast(),
      limit,
      offset,
    } : {
      minLat: bounds.minLat,
      maxLat: bounds.maxLat,
      minLng: bounds.minLng,
      maxLng: bounds.maxLng,
      limit,
      offset,
    };
    return api.get('/routes/area', { params });
  },
  getVideosInArea: (bounds, limit, offset) => {
    // Поддерживаем оба формата: Leaflet bounds объект и простой объект
    const params = bounds.getSouth ? {
      minLat: bounds.getSouth(),
      maxLat: bounds.getNorth(),
      minLng: bounds.getWest(),
      maxLng: bounds.getEast(),
    } : {
      minLat: bounds.minLat,
      maxLat: bounds.maxLat,
      minLng: bounds.minLng,
      maxLng: bounds.maxLng,
    };
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    return api.get('/videos/area', { params });
  },
};

// Video API
export const videoAPI = {
  getVideo: (id, locale = null) => {
    // Добавляем locale как query параметр для инвалидации кеша браузера
    const url = locale ? `/videos/${id}?locale=${locale}&_t=${Date.now()}` : `/videos/${id}?_t=${Date.now()}`;
    return api.get(url);
  },
  getVideosByRoute: (routeId, locale = null, cacheBuster = null) => {
    // Добавляем locale и cacheBuster как query параметры для инвалидации кеша браузера
    const params = new URLSearchParams();
    if (locale) {
      params.append('locale', locale);
    }
    // Используем переданный cacheBuster или генерируем новый
    params.append('_t', (cacheBuster || Date.now()).toString());
    const url = `/videos/route/${routeId}?${params.toString()}`;
    return api.get(url);
  },
  getVideosByRoutes: (routeIds, locale = null) => {
    if (!Array.isArray(routeIds) || routeIds.length === 0) {
      return Promise.resolve({ data: {} });
    }
    const ids = routeIds.slice(0, 50).join(',');
    const params = new URLSearchParams({ route_ids: ids });
    if (locale) params.append('locale', locale);
    return api.get(`/videos/by-routes?${params.toString()}`);
  },
  getUserVideos: (limit = 9, offset = 0) => api.get(`/videos/my?limit=${limit}&offset=${offset}`),
  getPublicUserVideos: (userId, limit = 9, offset = 0) => api.get(`/users/${userId}/videos?limit=${limit}&offset=${offset}`),
  createVideo: (data) => api.post('/videos', data),
  updateVideo: (id, data) => api.put(`/videos/${id}`, data),
  likeVideo: (id) => api.post(`/videos/${id}/like`),
  deleteVideo: (id) => api.delete(`/videos/${id}`),
  regenerateThumbnail: (id) => api.post(`/videos/${id}/regenerate-thumbnail`),
  registerView: (id, data) => api.post(`/videos/${id}/view`, data),
  suggest: (query, limit = 10) => {
    const params = new URLSearchParams({ query, limit: limit.toString() });
    return api.get(`/videos/suggest?${params.toString()}`);
  },
  search: (query, limit = 20, offset = 0) => {
    const params = new URLSearchParams({ query, limit: limit.toString(), offset: offset.toString() });
    return api.get(`/videos/search?${params.toString()}`);
  },
};

// Route API
export const routeAPI = {
  getRoute: (id, locale = null, cacheBuster = null) => {
    // Добавляем locale и cacheBuster как query параметры для инвалидации кеша браузера
    const params = new URLSearchParams();
    if (locale) {
      params.append('locale', locale);
    }
    // Используем переданный cacheBuster или генерируем новый
    params.append('_t', (cacheBuster || Date.now()).toString());
    const url = `/routes/${id}?${params.toString()}`;
    return api.get(url);
  },
  getUserRoutes: (limit = 15, offset = 0, isPublic = null) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (isPublic !== null) {
      params.append('is_public', isPublic.toString());
    }
    return api.get(`/routes/my?${params.toString()}`);
  },
  getPublicUserRoutes: (userId, limit = 15, offset = 0) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    return api.get(`/users/${userId}/routes?${params.toString()}`);
  },
  createRoute: (data) => api.post('/routes', data),
  updateRoute: (id, data) => api.put(`/routes/${id}`, data),
  deleteRoute: (id) => api.delete(`/routes/${id}`),
  registerView: (id, data) => api.post(`/routes/${id}/view`, data),
};

// User API
export const userAPI = {
  getUser: (id) => api.get(`/users/${id}`),
  searchUsers: (query) => api.get(`/users/search?query=${query}`),
  getNotificationSettings: () => api.get('/user/notification-settings'),
  updateNotificationSettings: (settings) => api.put('/user/notification-settings', { settings }),
  updateNotificationSetting: (type, data) => api.put(`/user/notification-settings/${type}`, data),
  changePassword: (data) => api.post('/users/change-password', data),
  getUserStats: () => api.get('/users/stats'),
  uploadAvatar: (formData) => api.post('/users/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  getViewHistory: ({ limit = 100, offset = 0 } = {}) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    return api.get(`/user/view-history?${params.toString()}`);
  },
};

// Video Position API (для кросс-платформенной синхронизации)
export const videoPositionAPI = {
  // Сохранить позицию просмотра видео
  savePosition: (videoId, position, url, clientTimestamp = null) => {
    const payload = {
      position: position,
      url: url
    };
    // Добавляем video_id только если он не null/undefined
    if (videoId !== null && videoId !== undefined) {
      payload.video_id = videoId;
    }
    // Добавляем timestamp клиента для сравнения на сервере
    if (clientTimestamp !== null && clientTimestamp !== undefined) {
      payload.client_timestamp = clientTimestamp;
    }
    return api.post('/user/video-positions', payload);
  },
  
  // Получить позицию просмотра видео
  getPosition: (videoId, url) => {
    const params = new URLSearchParams();
    if (videoId) params.append('video_id', videoId);
    if (url) params.append('url', url);
    return api.get(`/user/video-positions?${params.toString()}`);
  },
  
  // Получить все позиции пользователя
  getAllPositions: () => {
    return api.get('/user/video-positions');
  },
  
  // Удалить позицию
  deletePosition: (videoId, url) => {
    const params = new URLSearchParams();
    if (videoId) params.append('video_id', videoId);
    if (url) params.append('url', url);
    return api.delete(`/user/video-positions?${params.toString()}`);
  },
};

// Admin API
export const adminAPI = {
  getPendingVideos: (limit = 50, offset = 0) => api.get(`/admin/videos/pending?limit=${limit}&offset=${offset}`),
  getAllVideos: (limit = 50, offset = 0, cacheBust = false) => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (cacheBust) params.set('_t', Date.now().toString());
    return api.get(`/admin/videos/all?${params.toString()}`);
  },
  approveVideo: (id) => api.post(`/admin/videos/${id}/approve`),
  rejectVideo: (id) => api.post(`/admin/videos/${id}/reject`),
  setVideoHasFaces: (id, hasFaces) => api.put(`/admin/videos/${id}/has-faces`, { has_faces: Boolean(hasFaces) }),
  getAllUsers: (limit = 50, offset = 0, search = '') => {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (search) params.append('search', search);
    return api.get(`/admin/users?${params.toString()}`);
  },
  updateUserAdminStatus: (userId, isAdmin) => api.put(`/admin/users/${userId}/admin`, { is_admin: Boolean(isAdmin) }),
  updateUserBanStatus: (userId, isBanned) => api.put(`/admin/users/${userId}/ban`, { is_banned: isBanned }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getAdminStats: () => api.get('/admin/stats'),
  getAllRoutes: (limit = 50, offset = 0) => api.get(`/admin/routes?limit=${limit}&offset=${offset}`),
  reassignRouteOwner: (routeId, newOwnerId) => api.put(`/admin/routes/${routeId}/reassign-owner`, { user_id: newOwnerId }),
  // UI settings
  getUiSettings: () => api.get('/admin/ui-settings'),
  updateUiSettings: (payload) => api.put('/admin/ui-settings', payload),
  getUsersLoginMeta: (ids=[]) => api.get(`/admin/users/login-meta?ids=${ids.join(',')}`),
  // Contact messages
  getContactMessages: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    return api.get(`/admin/contact-messages?${queryParams.toString()}`);
  },
  getContactMessage: (id) => api.get(`/admin/contact-messages/${id}`),
  updateContactMessage: (id, data) => api.put(`/admin/contact-messages/${id}`, data),
  createContactReply: (id, data) => api.post(`/admin/contact-messages/${id}/replies`, data),
  // Translations
  checkTranslations: (contentType, targetLangs = [], limit = 50, offset = 0) => {
    const params = new URLSearchParams({
      contentType: contentType,
      limit: limit.toString(),
      offset: offset.toString(),
    });
    if (targetLangs.length > 0) {
      params.append('target_langs', targetLangs.join(','));
    }
    return api.get(`/admin/translations/check?${params.toString()}`);
  },
  translateContent: (contentType, contentIDs, targetLangs = []) => {
    return api.post('/admin/translations/translate', {
      content_type: contentType,
      content_ids: contentIDs,
      target_langs: targetLangs,
    });
  },
  // YouTube Agent (proxy to agent service when YOUTUBE_AGENT_URL is set)
  youtubeAgent: {
    getLimit: () => api.get('/admin/youtube-agent/limit'),
    getHealth: () => api.get('/admin/youtube-agent-proxy/health'),
    setLimit: (videosPerDay) => api.patch('/admin/youtube-agent/limit', { videos_per_day: videosPerDay }),
    saveBotCredentials: (email, password) => api.post('/admin/youtube-agent/bot-credentials', { email, password }),
    checkBotAuth: () => api.get('/admin/youtube-agent/check-auth'),
    getQuotas: () => api.get('/admin/youtube-agent-proxy/quotas'),
    getQueue: (params = {}) => {
      const q = new URLSearchParams();
      if (params.status) q.set('status', params.status);
      if (params.limit) q.set('limit', params.limit);
      if (params.offset) q.set('offset', params.offset);
      return api.get(`/admin/youtube-agent-proxy/queue?${q.toString()}`);
    },
    submit: (url) => api.post('/admin/youtube-agent-proxy/submit', { url }),
    run: () => api.post('/admin/youtube-agent-proxy/run'),
    retry: (id) => api.post(`/admin/youtube-agent-proxy/submissions/${id}/retry`),
    deleteSubmission: (id) => api.delete(`/admin/youtube-agent-proxy/submissions/${id}`),
    getStats: () => api.get('/admin/youtube-agent-proxy/stats'),
  },
};

// Social API
export const socialAPI = {
  followUser: (userId) => api.post('/social/follow', { user_id: userId }),
  unfollowUser: (userId) => api.post('/social/unfollow', { user_id: userId }),
  checkFollowStatus: (userId) => api.get(`/social/follow/status/${userId}`),
  getFollowers: (userId) => api.get(`/social/followers/${userId}`),
  getFollowing: (userId) => api.get(`/social/following/${userId}`),
  createComment: (data) => api.post('/social/comments', data),
  getComments: (contentType, contentId) => api.get(`/social/comments/${contentType}/${contentId}`),
  updateComment: (id, data) => api.put(`/social/comments/${id}`, data),
  deleteComment: (id) => api.delete(`/social/comments/${id}`),
  likeContent: (contentType, contentId) => api.post(`/social/like/${contentType}/${contentId}`),
  unlikeContent: (contentType, contentId) => api.delete(`/social/like/${contentType}/${contentId}`),
  getLikes: (contentType, contentId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return api.get(`/social/likes/${contentType}/${contentId}${queryString ? `?${queryString}` : ''}`);
  },
  getLikesBatch: (contentType, contentIds) => {
    if (!Array.isArray(contentIds) || contentIds.length === 0) {
      return Promise.resolve({ data: {} });
    }
    const ids = contentIds.slice(0, 100).join(',');
    return api.get(`/social/likes/batch?content_type=${encodeURIComponent(contentType)}&content_ids=${ids}`);
  },
  // Friends API
  sendFriendRequest: (userId) => {
    if (!userId || typeof userId !== 'number') {
      return Promise.reject(new Error('Invalid user ID'));
    }
    return api.post('/social/friends/request', { user_id: userId });
  },
  acceptFriendRequest: (userId) => api.post('/social/friends/accept', { user_id: userId }),
  rejectFriendRequest: (userId) => api.post('/social/friends/reject', { user_id: userId }),
  cancelFriendRequest: (userId) => api.post('/social/friends/cancel', { user_id: userId }),
  removeFriend: (userId) => api.delete(`/social/friends/${userId}`),
  getFriends: (userId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return api.get(`/social/friends${queryString ? `?${queryString}` : ''}`);
  },
  getIncomingFriendRequests: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return api.get(`/social/friends/requests/incoming${queryString ? `?${queryString}` : ''}`);
  },
  getOutgoingFriendRequests: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return api.get(`/social/friends/requests/outgoing${queryString ? `?${queryString}` : ''}`);
  },
  getFriendshipStatus: (userId) => api.get(`/social/friends/status/${userId}`),
  // Block API
  blockUser: (userId) => api.post('/social/block', { user_id: userId }),
  unblockUser: (userId) => api.post('/social/unblock', { user_id: userId }),
  getBlockedUsers: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return api.get(`/social/blocked${queryString ? `?${queryString}` : ''}`);
  },
  checkBlockStatus: (userId) => api.get(`/social/block/status/${userId}`),
  // Block authors (hide their content)
  blockAuthor: (authorId) => api.post('/social/block-author', { blocked_author_id: authorId }),
  unblockAuthor: (authorId) => api.post('/social/unblock-author', { blocked_author_id: authorId }),
  checkAuthorBlockStatus: (authorId) => api.get(`/social/block-author/status/${authorId}`),
  getBlockedAuthors: () => api.get('/social/blocked-authors'),
  getFriendRequests: () => api.get('/social/friends/requests'),
  createGroup: (data) => api.post('/social/groups', data),
  getGroups: () => api.get('/social/groups'),
  getGroup: (id) => api.get(`/social/groups/${id}`),
  updateGroup: (id, data) => api.put(`/social/groups/${id}`, data),
  deleteGroup: (id) => api.delete(`/social/groups/${id}`),
  joinGroup: (id) => api.post(`/social/groups/${id}/join`),
  leaveGroup: (id) => api.post(`/social/groups/${id}/leave`),
  getGroupMembers: (id) => api.get(`/social/groups/${id}/members`),
  createReport: (data) => api.post('/social/reports', data),
  getReports: () => api.get('/social/reports'),
  updateReportStatus: (id, status) => api.put(`/social/reports/${id}/status`, { status }),
  blockContent: (contentType, contentId) => api.post(`/social/block/${contentType}/${contentId}`),
  unblockContent: (contentType, contentId) => api.delete(`/social/block/${contentType}/${contentId}`),
  getNotifications: (limit, offset) => {
    const params = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    return api.get('/social/notifications', { params });
  },
  markNotificationAsRead: (id) => api.put(`/social/notifications/${id}/read`),
  markAllNotificationsAsRead: () => api.put('/social/notifications/read-all'),
  sendMessage: (chatId, data) => api.post('/social/messages', { chatId, ...data }),
  // Новые методы для совместимости с messagesSlice
  getUserChats: async ({ limit, offset } = {}) => {
    const res = await api.get('/social/chats', { params: { limit, offset } });
    const chats = Array.isArray(res.data?.chats) ? res.data.chats : (Array.isArray(res.data) ? res.data : []);
    const hasMore = Boolean(res.data?.hasMore);
    return { data: { chats, hasMore } };
  },
  createChat: (userId) => api.post('/social/chats', { userId }),
  getChatMessages: async (chatId, { limit, offset } = {}) => {
    const res = await api.get(`/social/messages/${chatId}`, { params: { limit, offset } });
    const messages = Array.isArray(res.data?.messages) ? res.data.messages : (Array.isArray(res.data) ? res.data : []);
    return { data: messages };
  },
  getChats: () => api.get('/social/chats'),
  getMessages: (chatId) => api.get(`/social/messages/${chatId}`),
  markMessagesAsRead: (chatId, messageIds) => api.put(`/social/messages/${chatId}/read`, { messageIds }),
};

// Realtime/WS helpers exposed via HTTP endpoints
export const realtimeAPI = {
  getUserOnlineStatus: (userId) => api.get(`/ws/users/${userId}/online`),
};

// Hashtag API (бэкенд создаёт хештеги по именам при отсутствии и переводит ru/en)
export const hashtagAPI = {
  getHashtags: (params) => api.get('/hashtags', { params }),
  getTrendingHashtags: (params) => api.get('/hashtags/trending', { params }),
  searchHashtags: (query) => api.get(`/hashtags/search`, { params: { query } }),
  createHashtag: (data) => api.post('/hashtags', data),
  addHashtagToContent: (contentType, contentId, hashtags) =>
    api.post(`/hashtags/${contentType}/${contentId}`, {
      hashtag_names: Array.isArray(hashtags) ? hashtags : [hashtags],
    }),
  removeHashtagFromContent: (contentType, contentId, hashtagIdOrName) =>
    api.delete(`/hashtags/${contentType}/${contentId}/${encodeURIComponent(hashtagIdOrName)}`),
};

// Public stats API
export const statsAPI = {
  getStats: (unit = 'km') => api.get(`/stats?unit=${unit}`),
};

// Comments API
export const commentsAPI = {
  getComments: (contentType, contentId, limit = 50, offset = 0) => {
    const params = new URLSearchParams({
      content_type: contentType,
      content_id: contentId.toString(),
      limit: limit.toString(),
      offset: offset.toString(),
    });
    return api.get(`/comments?${params.toString()}`);
  },
  
  createComment: (data) => {
    // Преобразуем camelCase в snake_case для бэкенда
    const requestData = {
      content_type: data.contentType,
      content_id: data.contentId,
      text: data.text,
    };
    if (data.parentId !== null && data.parentId !== undefined) {
      requestData.parent_id = data.parentId;
    }
    return api.post('/comments', requestData);
  },
  
  updateComment: (id, data) => api.put(`/comments/${id}`, data),
  
  deleteComment: (id) => api.delete(`/comments/${id}`),
  
  likeComment: (id) => api.post(`/comments/${id}/like`),
  
  unlikeComment: (id) => api.delete(`/comments/${id}/like`),
  
  // Analytics
  trackVideoSourceClick: (videoId, sourceType, sourceUrl) => {
    return api.post('/analytics/video-source-click', {
      video_id: videoId,
      source_type: sourceType,
      source_url: sourceUrl,
    });
  },
  
  trackMapProviderUsage: (provider, duration, previousProvider) => {
    return api.post('/analytics/map-provider-usage', {
      provider: provider,
      duration: duration,
      previous_provider: previousProvider,
    });
  },
  
  // Personal analytics (for current user)
  getPersonalAnalytics: () => {
    return api.get('/user/analytics/personal');
  },
  
  // Author analytics (for any user)
  getAuthorAnalytics: (userId) => {
    return api.get(`/users/${userId}/analytics/author`);
  },
};

// Contact API
export const contactAPI = {
  createContactMessage: (data) => {
    return api.post('/contact', data);
  },
  getUserContactMessages: () => {
    return api.get('/user/contact-messages');
  },
  getContactReplies: (id) => {
    return api.get(`/user/contact-messages/${id}/replies`);
  },
  createContactReply: (id, data) => {
    return api.post(`/user/contact-messages/${id}/replies`, data);
  },
};

// Analytics API (отдельный объект для аналитики)
export const analyticsAPI = {
  trackMapProviderUsage: (provider, duration, previousProvider) => {
    return api.post('/analytics/map-provider-usage', {
      provider: provider,
      duration: duration,
      previous_provider: previousProvider,
    });
  },
  
  trackVideoSourceClick: (videoId, sourceType, sourceUrl) => {
    return api.post('/analytics/video-source-click', {
      video_id: videoId,
      source_type: sourceType,
      source_url: sourceUrl,
    });
  },
  
  getPersonalAnalytics: () => {
    return api.get('/user/analytics/personal');
  },
  
  getAuthorAnalytics: (userId) => {
    return api.get(`/users/${userId}/analytics/author`);
  },
};

/** Public settings (no auth): e.g. Yandex Metrika enabled + counter ID */
export const getPublicSettings = () => api.get('/public-settings');

export default api;
